// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { Flex, Input, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  useGetEnrollStatusQuery,
  useSendOtpTokenMutation,
  useVerifyOtpTokenMutation,
} from '../../api/hooks';
import DeviceHandoffComponent from '../../components/device-handoff/DeviceHandoffComponent';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import { isMobileDeviceDetected } from '../../utils/MobileDeviceHelper';
import { navigateWithContext } from '../../utils/NavigationHelper';
import './PhoneVerificationPage.less';
import type { OTPRef } from 'antd/lib/input/OTP';
import { useTranslation } from 'react-i18next';
import { Button, HexagonSpinner, Snackbar } from '@imprivata-cloud/patient-access-ui-components';

// =====================================
// Constants
// =====================================
const PHONE_PATTERN = /^\d{3}-\d{3}-\d{4}$/;
const SIX_DIGIT_OTP = /^\d{6}$/;
const OTP_EXPIRED_ERROR = 'otp expired';
// =====================================
// Utilities
// =====================================
const formatPhoneNumber = (value: string) => {
  // Extract only digits, ignoring all other characters
  const digits = value.replace(/\D/g, '').slice(0, 10); // cap at 10 digits

  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const normalizePhoneNumber = (phone: string) => phone.replace(/[\s\-()]/g, '');
// =====================================
// Subcomponents (pure, dumb)
// =====================================
function WarningBanner({ text }: { text?: string }) {
  if (!text) {
    return null;
  }
  return <Snackbar variant="warning" title={text} data-testid="warning-banner" />;
}
function PhoneNumberStep({
  phoneNumber,
  setPhoneNumber,
  canContinue,
  onContinue,
  errorText,
}: {
  phoneNumber: string;
  setPhoneNumber: (v: string) => void;
  canContinue: boolean;
  onContinue: () => void;
  errorText?: string;
}) {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  };
  const { t } = useTranslation();
  return (
    <Flex align="center" justify="center" vertical className="h-100 phone-number-step">
      <Typography.Title level={2} className="text-center" data-testid="phone-number-title">
        {t('phoneVerificationPage.whats-your-phone-number')}
      </Typography.Title>
      <Typography.Text className="text-center mb-16" data-testid="otp-send-text">
        {t('phoneVerificationPage.phone-needed-verify-identity-enrollment')}
      </Typography.Text>
      <Flex vertical className="w-100" gap={16}>
        {/* NOTE: deferred — antd Input with addonBefore retained pending PAUI Input addon support (PSA-9643 / REQ-011). */}
        <Input
          addonBefore="+1"
          type="text"
          value={phoneNumber}
          onChange={onChange}
          onPressEnter={onContinue}
          className="phone-input"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus
          data-testid="phone-number-input"
        />
        <Button
          variant="primary"
          label={t('phoneVerificationPage.continue')}
          onClick={onContinue}
          disabled={!canContinue}
          className="w-100"
          data-testid="phone-number-continue-button"
        />
        <WarningBanner text={errorText} />
      </Flex>
      <Typography.Text className="text-center mt-16" data-testid="otp-send-additional-text">
        {t('phoneVerificationPage.send-6digit-code-ensure-correct')}
      </Typography.Text>
    </Flex>
  );
}
function OtpStep({
  phoneNumber,
  otpToken,
  setOtpToken,
  canContinue,
  onContinue,
  onResend,
  onChangeNumber,
  inputRef,
  errorText,
  isCodeExpired,
}: {
  phoneNumber: string;
  otpToken: string;
  setOtpToken: (v: string) => void;
  canContinue: boolean;
  onContinue: () => void;
  onResend: () => void;
  onChangeNumber: () => void;
  inputRef: React.RefObject<OTPRef>;
  errorText?: string;
  isCodeExpired: boolean;
}) {
  const { t } = useTranslation();
  const displayErrorText = isCodeExpired ? undefined : errorText;
  // Set autocomplete attribute on OTP input fields for iOS Safari compatibility
  // Remove when https://github.com/ant-design/ant-design/issues/53188 fixed
  useEffect(() => {
    const setAutocompleteOnOtpInputs = () => {
      if (inputRef.current?.nativeElement) {
        const otpInputs = inputRef.current.nativeElement.querySelectorAll('input[type="text"]');
        otpInputs.forEach((input) => {
          (input as HTMLInputElement).setAttribute('autocomplete', 'one-time-code');
        });
      }
    };
    // Set immediately and also after a short delay to ensure DOM is ready
    setAutocompleteOnOtpInputs();
    const timeoutId = setTimeout(setAutocompleteOnOtpInputs, 100);
    return () => clearTimeout(timeoutId);
  }, [inputRef]);
  return (
    <Flex align="center" justify="center" vertical className="h-100 phone-number-step">
      <Typography.Title level={2} className="text-center" data-testid="otp-title">
        {t('phoneVerificationPage.confirm-your-phone-number')}
      </Typography.Title>
      <Typography.Text className="text-center mb-16" data-testid="otp-send-text">
        {t('phoneVerificationPage.enter-confirmation-code-sent-phone', { phoneNumber })}
        <br />
        {isCodeExpired ? (
          <span style={{ color: 'red' }}>{t('phoneVerificationPage.expired-code-resent')}</span>
        ) : (
          t('phoneVerificationPage.expiring-code-text')
        )}
      </Typography.Text>
      <Flex vertical className="w-100" gap={16}>
        <Flex justify="center">
          {/* NOTE: deferred — antd Input.OTP retained pending PAUI OTP primitive (PSA-9643 / REQ-011). */}
          <Input.OTP
            ref={inputRef}
            size="large"
            value={otpToken}
            onInput={(values) => {
              // onInput provides real-time updates for each character change
              // values is an array of individual input values
              const currentValue = values.join('');
              setOtpToken(currentValue);
            }}
            className="w-100"
            autoFocus={otpToken.length !== 6}
            inputMode="numeric"
            data-testid="otp-token-input"
          />
        </Flex>
        <Button
          variant="primary"
          label={t('phoneVerificationPage.continue')}
          onClick={onContinue}
          disabled={!canContinue}
          className="w-100"
          autoFocus={otpToken.length === 6}
          data-testid="otp-token-submit-button"
        />
        <Button
          variant="secondary"
          label={t('phoneVerificationPage.resend-confirmation-code')}
          onClick={onResend}
          className="w-100"
          data-testid="otp-token-resend-button"
        />
        <WarningBanner text={displayErrorText} />
      </Flex>
      <Typography.Text className="text-center mt-16">
        {t('phoneVerificationPage.wrong-number-text')}{' '}
        <button
          type="button"
          className="change-number-link"
          onClick={onChangeNumber}
          data-testid="otp-change-number-button"
        >
          {t('phoneVerificationPage.change-phone-number')}
        </button>
      </Typography.Text>
    </Flex>
  );
}
function EnrollmentLookup() {
  const { t } = useTranslation();
  return (
    <Flex vertical align="center" justify="center">
      <Typography.Title level={2} className="text-center mb-24">
        {t('phoneVerificationPage.were-searching-our-records-for-your-phone-number')}
      </Typography.Title>
      <HexagonSpinner data-testid="loading-spinner" />
    </Flex>
  );
}
// =====================================
// Main Component
// =====================================
const PhoneVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const otpInputRef = useRef<OTPRef>(null);
  const { t } = useTranslation();

  const MESSAGES = useMemo(
    () =>
      ({
        rateLimited: t('phoneVerificationPage.rate-limited'),
        genericSendFail: t('phoneVerificationPage.generic-send-fail'),
        verificationFailed: (phone: string) =>
          t('phoneVerificationPage.verification-failed', { phone }),
        unexpected: t('phoneVerificationPage.unexpected'),
        invalidPhone: t('phoneVerificationPage.invalid-phone'),
      }) as const,
    [t],
  );

  // Local state
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [otpToken, setOtpToken] = useState<string>('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isCodeExpired, setIsCodeExpired] = useState<boolean>(false);
  const [hasAttemptedPhoneSubmit, setHasAttemptedPhoneSubmit] = useState(false);
  // Derived state
  const isPhoneValid = useMemo(() => PHONE_PATTERN.test(phoneNumber), [phoneNumber]);
  const shouldShowPhoneValidation =
    !errorMessage && hasAttemptedPhoneSubmit && phoneNumber.length > 0 && !isPhoneValid;
  const phoneWarning = shouldShowPhoneValidation
    ? MESSAGES.invalidPhone
    : errorMessage && !otpId
      ? errorMessage
      : undefined;
  // Data hooks
  const {
    enrolled: isEnrolled,
    isLoading: isEnrollmentStatusLoading,
    isSuccess: isEnrollmentStatusSuccess,
    setEnabled: enableEnrollmentStatusQuery,
  } = useGetEnrollStatusQuery();
  const {
    verifyOtpToken,
    apiError: verifyOtpApiError,
    reset: resetVerifyOtpMutation,
    resetApiError: resetVerifyOtpApiError,
  } = useVerifyOtpTokenMutation({
    onSuccess: () => {
      setOtpVerified(true);
      enableEnrollmentStatusQuery();
    },
  });
  const {
    sendOtpToken,
    apiError: sendOtpApiError,
    reset: resetSendOtpMutation,
    resetApiError: resetSendOtpApiError,
  } = useSendOtpTokenMutation({
    onSuccess: (id: string) => {
      setOtpId(id);
      setErrorMessage(undefined);
      setTimeout(() => {
        focusOtpInput();
      }, 100);
    },
  });
  // -------------------------------------
  // Effects
  // -------------------------------------
  const focusOtpInput = useCallback(() => setTimeout(() => otpInputRef.current?.focus(), 0), []);
  useEffect(() => {
    const errorParam = searchParams.get('error-code');
    if (errorParam) {
      setErrorCode(errorParam);
    }
  }, [searchParams]);
  // Verify OTP error handling
  useEffect(() => {
    if (!verifyOtpApiError || !otpId) {
      return;
    }
    if (isCodeExpired && !errorMessage) {
      return;
    }
    const code = verifyOtpApiError.error.code as string | undefined;
    const message = verifyOtpApiError.error.message as string | undefined;
    if (code === 'verification-failed') {
      if (message?.toLowerCase().includes('otp expired')) {
        setErrorMessage(OTP_EXPIRED_ERROR);
        setIsCodeExpired(true);
        setOtpToken('');
      } else {
        setErrorMessage(MESSAGES.verificationFailed(phoneNumber));
        setOtpToken('');
        focusOtpInput();
      }
      return;
    }
    if (code === 'max-attempts-exceeded' || code === 'rate-limit-exceeded') {
      setOtpId(null); // back to phone entry
      setOtpToken('');
      setErrorMessage(MESSAGES.rateLimited);
      return;
    }
    setErrorMessage(message || MESSAGES.unexpected);
    setOtpToken('');
    focusOtpInput();
  }, [verifyOtpApiError, otpId, phoneNumber, focusOtpInput, isCodeExpired, errorMessage]);
  // Send OTP error handling
  useEffect(() => {
    if (!sendOtpApiError) {
      return;
    }
    const code = sendOtpApiError.error.code as string | undefined;
    if (code === 'rate-limit-exceeded') {
      setOtpId(null);
      setOtpToken('');
      setErrorMessage(MESSAGES.rateLimited);
    } else if (
      code === 'session-not-found' ||
      code === 'unauthorized' ||
      code === 'missing-tenant-id'
    ) {
      setErrorCode(code);
    } else {
      setErrorMessage(MESSAGES.genericSendFail);
    }
  }, [sendOtpApiError]);
  // Check enrollment after OTP verification
  useEffect(() => {
    if (!otpVerified || !isEnrollmentStatusSuccess) {
      return;
    }
    if (isEnrolled) {
      navigateWithContext(navigate, '/verify');
    }
  }, [otpVerified, isEnrollmentStatusSuccess, isEnrolled, navigate]);
  // -------------------------------------
  // Handlers (small, focused)
  // -------------------------------------
  const clearErrors = useCallback(() => {
    setErrorMessage(undefined);
    setIsCodeExpired(false);
  }, []);
  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setHasAttemptedPhoneSubmit(false);
      setPhoneNumber(value);
      // Clear errors when user completes typing a valid phone number
      if (PHONE_PATTERN.test(value)) {
        clearErrors();
        resetSendOtpApiError();
      }
    },
    [clearErrors, resetSendOtpApiError],
  );
  const sendOtp = useCallback(() => {
    setHasAttemptedPhoneSubmit(true);
    if (!isPhoneValid) {
      return;
    }
    clearErrors();
    resetVerifyOtpMutation();
    resetVerifyOtpApiError();
    resetSendOtpMutation();
    resetSendOtpApiError();
    const normalized = normalizePhoneNumber(phoneNumber);
    sendOtpToken(`+1${normalized}`);
  }, [
    clearErrors,
    phoneNumber,
    resetSendOtpMutation,
    resetSendOtpApiError,
    resetVerifyOtpMutation,
    resetVerifyOtpApiError,
    sendOtpToken,
    isPhoneValid,
  ]);
  const handleVerifyOtp = useCallback(() => {
    if (otpId && SIX_DIGIT_OTP.test(otpToken)) {
      verifyOtpToken(otpId, otpToken);
    }
  }, [otpId, otpToken, verifyOtpToken]);
  const handleResendOtp = useCallback(() => {
    clearErrors();
    setOtpToken('');
    resetSendOtpMutation();
    resetVerifyOtpMutation();
    resetVerifyOtpApiError();
    const normalized = normalizePhoneNumber(phoneNumber);
    sendOtpToken(`+1${normalized}`);
  }, [
    clearErrors,
    phoneNumber,
    resetSendOtpMutation,
    resetVerifyOtpMutation,
    resetVerifyOtpApiError,
    sendOtpToken,
  ]);
  const onOtpChange = useCallback(
    (value: string) => {
      setOtpToken(value || '');
      // Clear errors when user completes typing a valid 6-digit OTP
      if (SIX_DIGIT_OTP.test(value)) {
        clearErrors();
        resetVerifyOtpApiError();
      }
    },
    [clearErrors, resetVerifyOtpApiError],
  );
  const handleChangeNumber = useCallback(() => {
    setOtpId(null);
    setOtpToken('');
    setOtpVerified(false);
    clearErrors();
    resetSendOtpMutation();
    resetVerifyOtpMutation();
  }, [clearErrors, resetSendOtpMutation, resetVerifyOtpMutation]);
  // =====================================
  // Render (flattened, early returns)
  // =====================================
  if (errorCode) {
    return <ErrorComponent cause={errorCode} />;
  }
  if (!isMobileDeviceDetected()) {
    return <DeviceHandoffComponent />;
  }
  // Step 2: After sending OTP
  if (otpId) {
    const showLookup = isEnrollmentStatusLoading || (isEnrollmentStatusSuccess && isEnrolled);
    if (showLookup) {
      return <EnrollmentLookup />;
    }
    if (otpVerified && !isEnrolled && isEnrollmentStatusSuccess) {
      navigateWithContext(navigate, '/account');
    }
    const canContinue = SIX_DIGIT_OTP.test(otpToken) && !errorMessage;
    return (
      <OtpStep
        phoneNumber={phoneNumber}
        otpToken={otpToken}
        setOtpToken={onOtpChange}
        canContinue={canContinue}
        onContinue={handleVerifyOtp}
        onResend={handleResendOtp}
        onChangeNumber={handleChangeNumber}
        inputRef={otpInputRef as React.RefObject<OTPRef>}
        errorText={errorMessage}
        isCodeExpired={isCodeExpired}
      />
    );
  }
  return (
    <PhoneNumberStep
      phoneNumber={phoneNumber}
      setPhoneNumber={handlePhoneNumberChange}
      canContinue={phoneNumber.length > 0}
      onContinue={sendOtp}
      errorText={phoneWarning}
    />
  );
};
export default PhoneVerificationPage;
